import { Link } from 'react-router-dom';

/**
 * KVKK Aydınlatma Metni — public route /kvkk.
 *
 * Register sayfasındaki "KVKK Aydınlatma Metni" linki bu sayfaya açılır
 * (target=_blank). Hem auth olmamış kullanıcılar hem de login olmuş
 * kullanıcılar görebilir (App.tsx'te tüm route guard'larının dışında).
 *
 * İçerik kaynağı: app/docs/guvenlik/kvkk/aydinlatma-metni.md (v1.0).
 * Esaslı değişiklikte iki yerde de güncellemek gerek — daha sonra bir
 * markdown-loader veya CMS'e bağlanabilir. Şu an statik JSX yeterli.
 */
export function KvkkAydinlatmaPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="text-lg font-semibold tracking-tight text-primary">
            Zoprey CRM
          </Link>
          <Link
            to="/auth/login"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Giriş
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12">
        <article className="prose prose-sm max-w-none">
          <h1 className="text-3xl font-bold tracking-tight">
            KVKK Aydınlatma Metni
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Son güncellenme: 2026-10-04 · v1.1
          </p>

          <h2 className="mt-10 text-xl font-semibold">1. Veri Sorumlusu</h2>
          <p className="mt-2 text-sm">
            <strong>Şirket Unvanı:</strong> [Firma Adı — netleşince güncellenecek]
            <br />
            <strong>İletişim:</strong> kvkk@crmpro.tr
          </p>
          <p className="mt-2 text-sm">
            Veri Sorumlusu (bundan sonra "Şirket"), 6698 sayılı Kişisel
            Verilerin Korunması Kanunu (KVKK) uyarınca kişisel verileri aşağıda
            açıklanan amaçlarla hukuka uygun biçimde işlemektedir.
          </p>

          <h2 className="mt-8 text-xl font-semibold">
            2. İşlenen Kişisel Veri Kategorileri
          </h2>
          <h3 className="mt-4 text-base font-semibold">
            2.1 Kullanıcı (Müşterimiz) Verileri
          </h3>
          <ul className="mt-2 list-disc pl-6 text-sm">
            <li>
              <strong>Kimlik:</strong> Ad, soyad, e-posta, telefon, firma adı
            </li>
            <li>
              <strong>Kullanım:</strong> IP adresi, kullanıcı ajanı, son giriş
              tarihi, oturum bilgileri
            </li>
            <li>
              <strong>Faturalama (Faz 2):</strong> Ödeme tutarı, fatura adresi,
              vergi numarası
            </li>
          </ul>
          <h3 className="mt-4 text-base font-semibold">
            2.2 Sizin Müşterilerinize Ait Veriler ("Veri İşleyen" Rolü)
          </h3>
          <p className="mt-2 text-sm">
            CRM hesabınıza yüklediğiniz <strong>kendi müşterilerinizin</strong>{' '}
            verileri için Şirket <em>Veri İşleyen</em>'dir, Veri Sorumlusu
            sizsiniz. Bu verilerin toplanma süreci, açık rıza alınması ve
            aydınlatma yükümlülüğü size aittir.
          </p>
          <h3 className="mt-4 text-base font-semibold">
            2.3 Meta Yönetim Modülü (Instagram ve WhatsApp Mesajları)
          </h3>
          <p className="mt-2 text-sm">
            Meta Yönetim modülünü açıp işletmenizin Instagram profesyonel
            hesabını ya da WhatsApp Business numarasını bağladığınızda,
            müşterilerinizin bu hesaba gönderdiği mesajlar CRM hesabınıza
            aktarılır. Bu veriler için de
            Şirket <em>Veri İşleyen</em>, siz <em>Veri Sorumlusu</em>sunuz.
          </p>
          <ul className="mt-2 list-disc pl-6 text-sm">
            <li>
              <strong>İşlenen veriler:</strong> Mesaj gönderen kişinin Instagram
              kimliği, adı ve kullanıcı adı ya da WhatsApp telefon numarası ve
              profil adı; mesaj metinleri, eklerin bağlantıları ve mesaj
              zamanları; sizin gönderdiğiniz cevaplar
            </li>
            <li>
              <strong>Amaç:</strong> Mesajların tek ekrandan görülüp
              cevaplanması, müşteri ve fırsat kaydı oluşturulması, teklif
              hazırlanması
            </li>
            <li>
              <strong>Bağlantı anahtarı:</strong> Meta'nın verdiği erişim
              anahtarı şifrelenerek (AES-256-GCM) saklanır, düz metin tutulmaz
            </li>
            <li>
              <strong>Silme:</strong> Meta üzerinden uygulama izni
              kaldırıldığında bağlantı kapatılır ve erişim anahtarı silinir;
              Meta üzerinden veri silme talebi geldiğinde ayrıca bağlantıya ait
              konuşmalar ve mesajlar silinir
            </li>
            <li>
              <strong>WhatsApp — yalnız reklam sohbetleri:</strong> Bu seçenek
              açıkken (varsayılan) yalnız Meta reklamından başlayan sohbetler
              aktarılır; numaranın diğer sohbetleri, eski sohbet geçmişi ve
              rehber alınmaz. Gelen mesajlar işlendikten sonra sunucu kuyruğunda
              tutulmaz.
            </li>
          </ul>

          <h2 className="mt-8 text-xl font-semibold">3. Veri İşleme Amaçları</h2>
          <ul className="mt-2 list-disc pl-6 text-sm">
            <li>CRM hizmetinin sunulması ve devam ettirilmesi (sözleşmenin ifası)</li>
            <li>Hesap güvenliği ve dolandırıcılık önleme (meşru menfaat)</li>
            <li>Yasal yükümlülüklerin yerine getirilmesi (vergi, ticari defter)</li>
            <li>Müşteri desteği ve teknik yardım</li>
            <li>Hizmet iyileştirme ve istatistiksel analiz (anonimleştirilmiş)</li>
            <li>(Açık rızanız varsa) Pazarlama iletişimleri</li>
          </ul>

          <h2 className="mt-8 text-xl font-semibold">4. Verilerin Aktarılması</h2>
          <p className="mt-2 text-sm">
            <strong>Yurt içi:</strong> Türkiye'deki veritabanı sunucumuz +
            yetkili çalışanlarımız ("need-to-know" prensibi).
          </p>
          <p className="mt-2 text-sm">
            <strong>Yurt dışı (SCC + DPA çerçevesinde):</strong>
          </p>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="border px-3 py-2 text-left">Hizmet</th>
                  <th className="border px-3 py-2 text-left">Sağlayıcı</th>
                  <th className="border px-3 py-2 text-left">Lokasyon</th>
                  <th className="border px-3 py-2 text-left">Aktarılan</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border px-3 py-2">Kimlik doğrulama</td>
                  <td className="border px-3 py-2">Supabase Inc.</td>
                  <td className="border px-3 py-2">AB (Frankfurt)</td>
                  <td className="border px-3 py-2">E-posta, şifre hash, oturum</td>
                </tr>
                <tr>
                  <td className="border px-3 py-2">Hata izleme</td>
                  <td className="border px-3 py-2">Sentry GmbH</td>
                  <td className="border px-3 py-2">AB / ABD</td>
                  <td className="border px-3 py-2">Hata mesajları (anonimleştirilmiş)</td>
                </tr>
                <tr>
                  <td className="border px-3 py-2">E-posta servisi</td>
                  <td className="border px-3 py-2">(SMTP sağlayıcı)</td>
                  <td className="border px-3 py-2">ABD</td>
                  <td className="border px-3 py-2">E-posta gönderim verisi</td>
                </tr>
                <tr>
                  <td className="border px-3 py-2">Instagram ve WhatsApp mesajları (Meta Yönetim, yalnız modülü açan firmalar)</td>
                  <td className="border px-3 py-2">Meta Platforms Ireland Ltd.</td>
                  <td className="border px-3 py-2">AB / ABD</td>
                  <td className="border px-3 py-2">Gönderilen cevap mesajları, bağlantı anahtarı ile yapılan istekler</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="mt-8 text-xl font-semibold">
            5. Kişisel Veri Sahibinin Hakları (KVKK m. 11)
          </h2>
          <ul className="mt-2 list-disc pl-6 text-sm">
            <li>Verilerinizin işlenip işlenmediğini öğrenme</li>
            <li>Verilerinizin bir kopyasını talep etme</li>
            <li>Yanlış verilerin düzeltilmesini talep etme</li>
            <li>Hesabınızı kapatma (30 gün soft delete → kalıcı silme)</li>
            <li>Pazarlama mesajlarına itiraz (her e-postada opt-out linki)</li>
            <li>Verilerinizin kimlere aktarıldığını öğrenme</li>
            <li>Verilerinizi JSON/CSV olarak export etme (veri taşınabilirliği)</li>
          </ul>
          <p className="mt-3 text-sm">
            Bu hakları kullanmak için: <strong>kvkk@crmpro.tr</strong> veya hesap
            ayarlarınızdan ilgili eylemleri gerçekleştirin. Talepleriniz en geç{' '}
            <strong>30 gün</strong> içinde ücretsiz yanıtlanır.
          </p>

          <h2 className="mt-8 text-xl font-semibold">6. Veri Saklama Süreleri</h2>
          <ul className="mt-2 list-disc pl-6 text-sm">
            <li>Aktif hesap: Hesap aktif olduğu sürece</li>
            <li>Soft delete sonrası: 30 gün</li>
            <li>Faturalama: 10 yıl (TTK m. 82)</li>
            <li>Audit log: 5 yıl (KVKK m. 12 — güvenlik)</li>
            <li>Pazarlama izin kayıtları: İzin geri alınana kadar + 3 yıl</li>
            <li>Instagram ve WhatsApp mesajları (Meta Yönetim): Hesap aktif olduğu sürece; Meta üzerinden veri silme talebinde silinir</li>
          </ul>

          <h2 className="mt-8 text-xl font-semibold">7. Veri Güvenliği</h2>
          <ul className="mt-2 list-disc pl-6 text-sm">
            <li>TLS 1.2+ ile şifrelenmiş trafik</li>
            <li>Veritabanı seviyesinde Row Level Security (RLS) — tenant izolasyonu</li>
            <li>Hassas alanlar için ek şifreleme (vergi no, TC kimlik)</li>
            <li>Şifreler Argon2id ile hash</li>
            <li>Tüm değişiklikler audit log'a kaydedilir</li>
            <li>Yıllık çalışan KVKK eğitimi + 6 aylık penetrasyon testi</li>
            <li>İhlal durumunda 72 saat içinde KVKK Kurumu'na bildirim</li>
          </ul>

          <h2 className="mt-8 text-xl font-semibold">
            8. Bu Metnin Güncellenmesi
          </h2>
          <p className="mt-2 text-sm">
            Bu Aydınlatma Metni mevzuat veya hizmet değişikliklerine bağlı
            olarak güncellenebilir. Esaslı değişiklikler en az{' '}
            <strong>30 gün önceden</strong> e-posta ile bildirilir.
          </p>

          <hr className="my-10" />

          <p className="text-xs text-muted-foreground">
            İletişim: kvkk@crmpro.tr · KVKK Kurulu:{' '}
            <a
              href="https://www.kvkk.gov.tr"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              kvkk.gov.tr
            </a>
          </p>
        </article>
      </main>
    </div>
  );
}
